import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Calendar, 
  Users, 
  Bell, 
  TrendingUp, 
  MapPin, 
  Clock,
  UserPlus,
  FileText,
  Target,
  Plus
} from "lucide-react";
import { getPositionLabel } from "@/utils/positions";

const CaptainDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Açık pozisyon oluşturma state'leri
  const [showPositionDialog, setShowPositionDialog] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [positionNotes, setPositionNotes] = useState("");
  const [creatingPositions, setCreatingPositions] = useState(false);

  const allPositions = [
    { id: "goalkeeper", label: "Kaleci" },
    { id: "defender", label: "Defans" },
    { id: "midfielder", label: "Orta Saha" },
    { id: "forward", label: "Forvet" },
    { id: "substitute", label: "Yedek" }
  ];

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/captain/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Dashboard yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const openPositionDialog = (match) => {
    setSelectedMatch(match);
    setSelectedPositions([]);
    setPositionNotes("");
    setShowPositionDialog(true);
  };

  const handlePositionToggle = (positionId) => {
    setSelectedPositions(prev => 
      prev.includes(positionId) 
        ? prev.filter(p => p !== positionId)
        : [...prev, positionId]
    );
  };

  const handleCreatePositions = async () => {
    if (selectedPositions.length === 0) {
      toast.error("En az bir pozisyon seçmelisiniz");
      return;
    }

    setCreatingPositions(true);
    try {
      await axios.post(`${API}/matches/${selectedMatch.id}/open-positions`, {
        positions: selectedPositions,
        notes: positionNotes
      });
      
      toast.success("Açık pozisyonlar oluşturuldu!");
      setShowPositionDialog(false);
      fetchDashboard(); // Refresh data
    } catch (error) {
      console.error("Error creating positions:", error);
      toast.error(error.response?.data?.detail || "Pozisyonlar oluşturulamadı");
    } finally {
      setCreatingPositions(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent mb-2">
            Kaptan Paneli
          </h1>
          <p className="text-gray-600">Takımını yönet, oyuncu bul, maçları organize et</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Yaklaşan Maçlar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-blue-600">
                  {dashboard?.upcomingMatches?.length || 0}
                </span>
                <Calendar className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Bekleyen Başvurular</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-orange-600">
                  {dashboard?.pendingApplicationsCount || 0}
                </span>
                <Bell className="h-8 w-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Açık Pozisyonlar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-purple-600">
                  {dashboard?.openPositionsCount || 0}
                </span>
                <Target className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Organize Edilen Maçlar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-green-600">
                  {dashboard?.totalMatchesOrganized || 0}
                </span>
                <TrendingUp className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Button 
            className="h-24 text-lg bg-gradient-to-r from-emerald-500 to-green-600 dark:from-dark-card dark:to-dark-accent"
            onClick={() => navigate("/player-pool")}
          >
            <Users className="h-6 w-6 mr-2" />
            Oyuncu Havuzu
          </Button>
          <Button 
            className="h-24 text-lg bg-gradient-to-r from-blue-500 to-blue-600"
            onClick={() => navigate("/captain/applications")}
          >
            <FileText className="h-6 w-6 mr-2" />
            Başvuruları Yönet ({dashboard?.pendingApplicationsCount || 0})
          </Button>
          <Button 
            className="h-24 text-lg bg-gradient-to-r from-purple-500 to-purple-600"
            onClick={() => navigate("/create-match")}
          >
            <UserPlus className="h-6 w-6 mr-2" />
            Maç Oluştur
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Matches */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Yaklaşan Maçlar
              </CardTitle>
              <CardDescription>Organize ettiğin maçlar</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.upcomingMatches?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Yaklaşan maç yok</p>
                  <Button 
                    variant="link" 
                    className="mt-2"
                    onClick={() => navigate("/")}
                  >
                    Maç oluştur
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboard?.upcomingMatches?.map((match) => (
                    <div 
                      key={match.id}
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/matches/${match.id}`)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{match.venueName}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {match.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {match.time}
                            </span>
                          </div>
                        </div>
                        {match.missingPlayersCount > 0 && (
                          <Badge variant="destructive">
                            {match.missingPlayersCount} Eksik
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            {match.players?.length || 0} / {match.teamSize || 14} Oyuncu
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPositionDialog(match);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Açık Pozisyon
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Applications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-orange-600" />
                Son Başvurular
              </CardTitle>
              <CardDescription>Değerlendirmen gereken başvurular</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.pendingApplications?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Bekleyen başvuru yok</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dashboard?.pendingApplications?.map((app) => (
                    <div 
                      key={app.id}
                      className="p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={app.userProfileImage} />
                          <AvatarFallback>{app.userName?.[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-semibold">{app.userName}</p>
                          <p className="text-sm text-gray-600">
                            {getPositionLabel(app.detailedPosition)}
                          </p>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-800">
                          {app.userPoints || 0} puan
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {dashboard?.pendingApplicationsCount > 10 && (
                    <Button 
                      variant="link" 
                      className="w-full"
                      onClick={() => navigate("/captain/applications")}
                    >
                      Tümünü Gör ({dashboard.pendingApplicationsCount})
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Açık Pozisyon Oluşturma Dialog */}
      <Dialog open={showPositionDialog} onOpenChange={setShowPositionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Açık Pozisyon Oluştur</DialogTitle>
          </DialogHeader>
          
          {selectedMatch && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold">{selectedMatch.venueName}</p>
                <p className="text-sm text-gray-600">
                  {selectedMatch.date} - {selectedMatch.time}
                </p>
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">
                  Hangi pozisyonlara oyuncu arıyorsunuz?
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {allPositions.map((pos) => (
                    <div
                      key={pos.id}
                      className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedPositions.includes(pos.id)
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => handlePositionToggle(pos.id)}
                    >
                      <Checkbox
                        id={pos.id}
                        checked={selectedPositions.includes(pos.id)}
                        onCheckedChange={() => handlePositionToggle(pos.id)}
                      />
                      <Label htmlFor={pos.id} className="cursor-pointer flex-1">
                        {pos.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="notes" className="text-sm font-medium">
                  Notlar (opsiyonel)
                </Label>
                <Textarea
                  id="notes"
                  placeholder="Örn: Tecrübeli oyuncu aranıyor..."
                  value={positionNotes}
                  onChange={(e) => setPositionNotes(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowPositionDialog(false)}
            >
              İptal
            </Button>
            <Button 
              onClick={handleCreatePositions}
              disabled={creatingPositions || selectedPositions.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {creatingPositions ? "Oluşturuluyor..." : `${selectedPositions.length} Pozisyon Oluştur`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaptainDashboard;
